import { useState, useEffect, useCallback } from "react";
import { useAtomValue } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { deleteTransaction, getTransactions, updateTransaction } from "@/api";
import { TransactionWithInstrumentAndAccount } from "@/types";
import { SelectedReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";

export type ModalType = "add" | "edit" | "view" | "delete" | null;

export function useTransactionsController() {
  const [transactions, setTransactions] = useState<
    TransactionWithInstrumentAndAccount[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionWithInstrumentAndAccount | null>(null);

  const selectedFund = useAtomValue(SelectedFundAtom);
  const selectedReportingPeriod = useAtomValue(SelectedReportingPeriodAtom);
  // Fetch transactions - mock implementation for now
  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!selectedFund) {
        setTransactions([]);
        setIsLoading(false);
        return;
      }
      if (!selectedReportingPeriod) {
        setTransactions([]);
        setIsLoading(false);
        return;
      }
      const result = await getTransactions(selectedFund.fundId, parseInt(selectedReportingPeriod.reportingPeriodId) || undefined);

      // Wait a short time to simulate API call
      setTimeout(() => {
        setTransactions(result);
        setIsLoading(false);
      }, 1000);
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setError("Failed to fetch transactions");
      setIsLoading(false);
    }
  }, [selectedFund, selectedReportingPeriod]);

  // Fetch transactions on component mount and when selected fund changes
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Filter transactions based on search query
  const filteredTransactions = transactions.filter((transaction) => {
    // if (!searchQuery.trim()) return true;

    // const search = searchQuery.toLowerCase();
    // return (
    //   transaction.description.toLowerCase().includes(search) ||
    //   transaction.type.toLowerCase().includes(search) ||
    //   transaction.amount.toString().includes(search) ||
    //   transaction.status.toLowerCase().includes(search) ||
    //   (transaction.accountName &&
    //     transaction.accountName.toLowerCase().includes(search))
    // );

    return true;
  });

  // Handle transaction confirmation status update
  const confirmTransaction = async (
    transaction: TransactionWithInstrumentAndAccount,
    isConfirmed: boolean
  ) => {
    try {
      const updatedTransaction = await updateTransaction({
        ...transaction,
        transactionId: transaction.transactionId,
        isConfirmed,
      });

      if (updatedTransaction) {
        // Update the local state with the updated transaction
        setTransactions((prev) =>
          prev.map((t) =>
            t.transactionId === transaction.transactionId
              ? { ...t, isConfirmed }
              : t
          )
        );
      }
    } catch (error) {
      console.error("Error updating transaction confirmation status:", error);
    }
  };

  // Modal management
  const openModal = (
    type: ModalType,
    transaction: TransactionWithInstrumentAndAccount | null = null
  ) => {
    setModalType(type);
    setSelectedTransaction(transaction);
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedTransaction(null);
  };

  const handleDeleteTransaction = async (transaction: TransactionWithInstrumentAndAccount) => {
    if (!selectedReportingPeriod) {
      console.error("No reporting period selected");
      return;
    }

    try {
      const deleted = await deleteTransaction(
        transaction.transactionId,
        parseInt(selectedReportingPeriod.reportingPeriodId)
      );

      if (deleted) {
        setTransactions((prev) => prev.filter((t) => t.transactionId !== transaction.transactionId));
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
    }
  };

  return {
    transactions,
    filteredTransactions,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    modalType,
    selectedTransaction,
    openModal,
    closeModal,
    fetchTransactions,
    confirmTransaction,
    deleteTransaction: handleDeleteTransaction,
  };
}
